import React, { useState } from 'react';
import ProductCard from '../../components/common/ProductCard';
import { useProductContext } from '../../context/ProductContext';
import { useShopContext } from '../../context/ShopContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { Link } from 'react-router-dom';

const StoreProfilePage: React.FC = () => {
  const { selectedStore, } = useShopContext();
  const { products, next, previous, fetchProducts, setSelectedProductMe } = useProductContext();
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'available' | 'unavailable'>('all');
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);

  if (!selectedStore) {
    return <div className="text-center py-20 text-lg font-semibold">لم يتم العثور على المتجر.</div>;
  }

  const theme = selectedStore.theme;
  const BASECOLOR = theme?.primary_color || '#4F46E5';
  const SECONDCOLOR = theme?.secondary_color || '#FF9900';

  const themeVars = {
    '--primary': BASECOLOR,
    '--secondary': SECONDCOLOR,
  } as React.CSSProperties;

  const storeBranchIds = selectedStore.branches.map((branch) => branch.id);
  const storeProducts = products.filter((product) => storeBranchIds.includes(product.branch.id));

  const filteredProducts = storeProducts.filter((product) => {
    if (availabilityFilter === 'available' && !product.is_available) return false;
    if (availabilityFilter === 'unavailable' && product.is_available) return false;
    if (selectedBranch !== 'all' && `${product.branch.id}` !== selectedBranch) return false;
    return true;
  });

  const featuredProducts = storeProducts.filter((product) => product.product.is_featured);

  const handlePageChange = (url: string | null, direction: 'next' | 'prev') => {
    if (!url) return;
    const searchParams = new URLSearchParams(url.split('?')[1]);
    fetchProducts(searchParams);
    setCurrentPage((prev) => (direction === 'next' ? prev + 1 : prev - 1));
  };

  return (
    <section className="pb-10 mb-4" style={themeVars}>
      {/* سلايدر المنتجات المميزة */}
      {featuredProducts.length > 0 && (
        <div className="relative h-[500px] md:h-[600px] w-full mb-16">
          <Swiper
            modules={[Navigation, Pagination, Autoplay, EffectFade]}
            effect="fade"
            speed={800}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 5000, pauseOnMouseEnter: true }}
            loop
            className="h-full w-full"
          >
            {featuredProducts.map((product) => (
              <SwiperSlide key={product.id}>
                <div className="relative h-full w-full">
                  <img
                    src={product?.product?.images[0]?.image || '/default-product.jpg'}
                    alt={product.product.images[0]?.alt_text}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center">
                    <div className="container mx-auto px-6 text-white">
                      <div className="max-w-lg">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">{product.product.name}</h2>
                        <p className="text-lg md:text-xl mb-6 line-clamp-2">
                          {product.product.description || 'وصف المنتج غير متوفر'}
                        </p>
                        <div className="flex items-center gap-4">
                          <span className="text-2xl font-bold">{product.price} جنيه</span>
                          {product.product.base_price && (
                            <span className="text-xl line-through text-gray-300">
                              {product.product.base_price} جنيه
                            </span>
                          )}
                        </div>
                        <Link
                          onClick={() => {
                            if (product !== undefined) {
                              setSelectedProductMe(product);
                            }
                          }}
                          to={`/product/${product.id}`}
                          className="mt-6 inline-block text-white font-medium py-3 px-8 rounded-lg transition duration-300 bg-[var(--primary)] hover:opacity-90"
                        >
                          عرض المنتج
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      <div className="px-8">
        {/* فلترة الفروع */}
        {selectedStore.branches.length > 1 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[var(--primary)] mb-6 text-center">تصفح حسب الفرع</h2>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setSelectedBranch('all')}
                className={`px-5 py-2.5 rounded-full font-medium transition-all border ${selectedBranch === 'all' ? 'text-white shadow-md bg-[var(--primary)]' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
              >
                جميع الفروع
              </button>
              {selectedStore.branches.map((branch) => (
                <button
                  key={branch.id}
                  onClick={() => setSelectedBranch(`${branch.id}`)}
                  className={`px-5 py-2.5 rounded-full font-medium transition-all border ${selectedBranch === `${branch.id}` ? 'text-white shadow-md bg-[var(--primary)]' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                >
                  {branch.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* قسم المنتجات مع الفلترة */}
        <div className="mb-16">
          <div className="flex flex-wrap items-center justify-between mb-8 gap-4">
            <h2 className="text-2xl font-bold text-[var(--primary)]">منتجات المتجر</h2>
            <div className="flex items-center gap-3">
              <label className="font-medium text-gray-700">التوفر:</label>
              <select
                value={availabilityFilter}
                onChange={(e) => setAvailabilityFilter(e.target.value as any)}
                className="border rounded-lg px-4 py-2.5 focus:outline-primary bg-white shadow-sm"
              >
                <option value="all">الكل</option>
                <option value="available">متاح فقط</option>
                <option value="unavailable">غير متاح</option>
              </select>
            </div>
          </div>

          {filteredProducts.length > 0 ? (
            <>
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Pagination Controls */}
              <div className="flex justify-center items-center gap-4 mt-12">
                <button
                  onClick={() => handlePageChange(previous, 'prev')}
                  disabled={!previous}
                  className={`px-6 py-3 rounded-lg text-white transition ${previous ? 'bg-[var(--primary)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'
                    }`}
                >
                  السابق
                </button>
                <span className="font-medium text-gray-700">صفحة {currentPage}</span>
                <button
                  onClick={() => handlePageChange(next, 'next')}
                  disabled={!next}
                  className={`px-6 py-3 rounded-lg text-white transition ${next ? 'bg-[var(--primary)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'
                    }`}
                >
                  التالي
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-16 rounded-xl bg-gray-50">
              <p className="text-gray-600 text-lg mb-4">لا توجد منتجات مطابقة للفلاتر المحددة.</p>
              <button
                onClick={() => {
                  setSelectedBranch('all');
                  setAvailabilityFilter('all');
                }}
                className="px-6 py-3 text-white rounded-lg bg-[var(--primary)] hover:opacity-90 transition shadow-md"
              >
                عرض كل المنتجات
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default StoreProfilePage;
